/**
 * Which documents an event may carry, and how big.
 *
 * The rules live here rather than in the component so the picker's `accept`,
 * the check on a dropped file and the message a person reads all come from one
 * list. A gallery that offers a type it then refuses is the usual way this goes
 * wrong.
 *
 * DOCUMENTS ONLY, deliberately narrower than the portal's uploader, which also
 * takes images, video and archives (see `useFileUpload`'s
 * DEFAULT_ALLOWED_TYPES). An event already has a banner for its picture, and
 * this list is what an organiser actually attaches: a risk assessment, a
 * costing, a running sheet, a permission form.
 */

/** 10MB, matching the portal's uploader. Anything larger is a link, not an attachment. */
const MAX_BYTES = 10 * 1024 * 1024;

/**
 * What the file picker offers, as MIME types for CCFileGallery's `accept`.
 *
 * Both the modern and the legacy Office types: a school's shared drives are
 * full of `.doc` and `.xls`, and refusing them would be refusing the documents
 * that actually exist.
 */
export const DOCUMENT_MIME_TYPES = [
	"application/pdf",
	"application/msword",
	"application/vnd.openxmlformats-officedocument.wordprocessingml.document",
	"application/vnd.ms-excel",
	"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
	"application/vnd.ms-powerpoint",
	"application/vnd.openxmlformats-officedocument.presentationml.presentation",
	"text/csv",
	"text/plain",
];

/**
 * The same list as extensions, which is what actually gets checked.
 *
 * A browser's `file.type` is guesswork from the extension and is routinely
 * EMPTY for Office files dragged from a network share — so a MIME-only check
 * rejects a perfectly good `.docx` with no explanation. The extension is the
 * thing the user can see and the thing that is always there.
 */
const ALLOWED_EXTENSIONS = [
	".pdf",
	".doc",
	".docx",
	".xls",
	".xlsx",
	".ppt",
	".pptx",
	".csv",
	".txt",
];

/** Thrown with a message written to be shown to the person who picked the file. */
export class EventDocumentError extends Error {}

const extensionOf = (name: string): string => {
	const dot = name.lastIndexOf(".");
	return dot === -1 ? "" : name.slice(dot).toLowerCase();
};

/**
 * Refuse a file before a byte of it is uploaded, or say nothing and let it
 * through.
 *
 * Size first: it is the cheaper test, and the message is the more useful one.
 */
export function checkDocument(file: File): void {
	if (file.size > MAX_BYTES) {
		throw new EventDocumentError(
			`"${file.name}" is ${Math.round(file.size / 1024 / 1024)}MB. Please attach files under 10MB.`,
		);
	}
	if (!ALLOWED_EXTENSIONS.includes(extensionOf(file.name))) {
		throw new EventDocumentError(
			`"${file.name}" is not a document. Attach a PDF, Word, Excel, PowerPoint, CSV or text file.`,
		);
	}
}

/** Bytes as something readable in a file row. */
export function fileSizeLabel(bytes?: number): string | undefined {
	if (!bytes || bytes < 0) return undefined;
	if (bytes < 1024) return `${bytes} B`;
	if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
	return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
